import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom';
import './SiteNav.css'
import {
  FOOTER_GROUPS,
  FooterGroup,
  findBySlug,
  footerGroupOf,
  indexRoute,
  isToolRoute,
  RouteSeo,
  routesInGroup,
  SITE_NAME,
} from '../Shared/Seo/routeSeo.ts';
import { useTranslation } from '../Shared/i18n/useTranslation.ts';
import { useLocale } from '../Shared/i18n/LocaleContext.tsx';

/**
 * How long a hovered menu stays open after the pointer leaves, so a diagonal
 * move from the button down to the list does not snap it shut on the way.
 */
const HOVER_CLOSE_MS = 150;

/**
 * The site header: the wordmark, a row of group menus, and a drawer holding
 * every page. It replaces the old "Menu" popup (ADR 0061 §6).
 *
 * Everything here is manifest data. The row is the footer groups that carry
 * `"nav": true` in routeSeo.json. Each one is a button that opens a list of
 * every page in its group - on hover for a mouse, on click or tap otherwise -
 * rather than a link to one page of it: "By grade" used to open kindergarten
 * and nothing else (ADR 0061 amendment). After the divider comes this locale's
 * index, the hub of the link graph ADR 0027 built.
 *
 * Below 900px the row folds away and the menu button opens a drawer. It opens
 * on a list of the sections - every footer group, in footer order, with its
 * page count - and a section opens as its own page of links behind a Back
 * button (ADR 0061, 2026-10-31 amendment). Listing every page at once made
 * a phone scroll more than ten screens to reach Games. The drawer is filtered
 * to the page's own locale, as the footer is (ADR 0051 §8): a Spanish page
 * lists the Spanish pages.
 *
 * `siteHeader` in scripts/prerender.js writes this header's closed state into
 * the static HTML, so its height is there from the first paint (ADR 0061,
 * 2026-09-29 amendment). Change the markup or its classes here and change them
 * there too.
 *
 * All real anchors, so every destination is crawlable, openable in a new tab
 * and middle-clickable. The drawer is a small hand-rolled dialog rather than
 * PrimeReact's Sidebar: it needs Escape, a scrim, focus in and focus back, and
 * nothing else, and a portal with transitions made every test asynchronous.
 */
export function SiteNav() {
  const t = useTranslation();
  const locale = useLocale();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  // The drawer section on show; null is the list of sections.
  const [section, setSection] = useState<string | null>(null);
  // The section last left with Back, so focus returns to its button.
  const leftSection = useRef<string | null>(null);
  const menuButton = useRef<HTMLButtonElement>(null);
  const drawer = useRef<HTMLDivElement>(null);

  const current = findBySlug(pathname);
  const currentGroup = current && isToolRoute(current) ? footerGroupOf(current) : null;
  const index = indexRoute(locale);

  const groups = FOOTER_GROUPS
    .map((group) => ({ group, pages: routesInGroup(group.key, locale) }))
    .filter(({ pages }) => pages.length > 0);

  const topGroups = groups.filter(({ group }) => group.nav);

  useEffect(() => {
    if (!open) {
      return;
    }
    drawer.current?.querySelector<HTMLElement>('a, button')?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    const button = menuButton.current;
    return () => {
      document.removeEventListener('keydown', onKey);
      button?.focus();
    };
  }, [open]);

  // Moving between the section list and a section swaps the drawer's whole
  // content, so focus would fall to the body. Into a section it goes to Back;
  // back out, to the button of the section just left.
  useEffect(() => {
    const panel = drawer.current;
    if (!panel) {
      return;
    }
    panel.scrollTop = 0;
    if (section) {
      panel.querySelector<HTMLElement>('.siteDrawerBack')?.focus();
    } else if (leftSection.current) {
      document.getElementById('drawerSection-' + leftSection.current)?.focus();
      leftSection.current = null;
    }
  }, [section]);

  const openSection = section ? groups.find(({ group }) => group.key === section) : undefined;

  return (
    <header className='siteHeader'>
      <div className='siteHeaderInner'>
        {/* WCAG 2.4.1: the first Tab stop jumps past the menus to the page's
            main landmark (ADR 0113 §2). Hidden until it has focus. */}
        <a className='skipLink' href='#main'>{t('nav.skipToContent')}</a>
        <button
          ref={menuButton}
          type='button'
          className='btn btn-ghost btn-icon siteMenuButton'
          aria-label={t('nav.openMenuAria')}
          aria-expanded={open}
          aria-controls='siteDrawer'
          onClick={() => {
            setSection(null);
            setOpen(true);
          }}
        >
          <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor'
            strokeWidth='1.5' strokeLinecap='round' aria-hidden='true' focusable='false'>
            <path d='M3 6h18'/><path d='M3 12h18'/><path d='M3 18h18'/>
          </svg>
        </button>
        <a className='siteBrand' href='/'>{SITE_NAME}</a>
        <nav className='siteNavLinks' aria-label={t('nav.siteNavAria')}>
          <NavMenus groups={topGroups} currentGroup={currentGroup} pathname={pathname}/>
          {index && (
            <>
              <span className='siteNavDivider' aria-hidden='true'/>
              <a className='siteNavIndex' href={index.slug} aria-current={pathname === index.slug ? 'page' : undefined}>
                {index.h1}
              </a>
            </>
          )}
        </nav>
      </div>

      {open && (
        <>
          <div className='siteDrawerScrim' onClick={() => setOpen(false)}/>
          <div
            ref={drawer}
            id='siteDrawer'
            className='siteDrawer'
            role='dialog'
            aria-modal='true'
            aria-label={t('nav.menuButtonLabel')}
          >
            <div className='siteDrawerHead'>
              <a className='siteBrand' href='/'>{t('nav.homeLabel')}</a>
              <button
                type='button'
                className='btn btn-ghost btn-icon'
                aria-label={t('nav.closeMenuAria')}
                onClick={() => setOpen(false)}
              >
                <span aria-hidden='true'>×</span>
              </button>
            </div>
            {openSection ? (
              <section className='siteDrawerGroup'>
                <button
                  type='button'
                  className='siteDrawerBack'
                  onClick={() => {
                    leftSection.current = openSection.group.key;
                    setSection(null);
                  }}
                >
                  <span aria-hidden='true'>‹</span> {t('nav.allSections')}
                </button>
                <h2 className='siteDrawerTitle'>{openSection.group.heading}</h2>
                <ul>
                  {openSection.pages.map((route) => (
                    <li key={route.slug}>
                      <a href={route.slug} aria-current={route.slug === pathname ? 'page' : undefined}>
                        {route.h1}
                      </a>
                    </li>
                  ))}
                </ul>
              </section>
            ) : (
              <>
                {index && (
                  <p className='siteDrawerIndex'>
                    <a href={index.slug} aria-current={pathname === index.slug ? 'page' : undefined}>
                      {index.h1}
                    </a>
                  </p>
                )}
                <ul className='siteDrawerSections'>
                  {groups.map(({ group, pages }) => (
                    <li key={group.key}>
                      <button
                        id={'drawerSection-' + group.key}
                        type='button'
                        className={'siteDrawerSection' + (group.key === currentGroup ? ' isCurrent' : '')}
                        onClick={() => setSection(group.key)}
                      >
                        <span className='siteDrawerSectionName'>{group.heading}</span>
                        <span className='siteDrawerSectionCount'>{pages.length}</span>
                        <span className='siteDrawerChevron' aria-hidden='true'>›</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </>
      )}
    </header>
  )
}

/**
 * The row of group menus: the disclosure-navigation pattern from the WAI-ARIA
 * practices, not a `role="menu"`. Each group is a button with `aria-expanded`
 * controlling a plain list of links, so a screen reader hears ordinary links
 * and Tab walks through them in order - there is no roving focus to learn.
 *
 * - A mouse opens a menu by hovering and closes it by leaving, after a short
 *   grace period.
 * - Click, tap, Enter or Space toggles it, so a touch screen or a keyboard
 *   reaches every page too. Hover is taken from pointer events of type
 *   "mouse" only: a tap fires the enter event as well, and treating that as
 *   hover would open the menu and then let the click close it again.
 * - Escape closes the open menu and puts focus back on its button; focus or a
 *   press landing outside the row closes it.
 *
 * Closed lists stay in the DOM, `hidden`, so every link is there for a crawler
 * that renders the page.
 */
function NavMenus({
  groups,
  currentGroup,
  pathname,
}: {
  groups: { group: FooterGroup; pages: RouteSeo[] }[];
  currentGroup: string | null;
  pathname: string;
}) {
  const [openKey, setOpenKey] = useState<string | null>(null);
  const row = useRef<HTMLUListElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const cancelClose = () => clearTimeout(closeTimer.current);
  const close = useCallback(() => {
    clearTimeout(closeTimer.current);
    setOpenKey(null);
  }, []);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  useEffect(() => {
    if (!openKey) {
      return;
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        document.getElementById('navButton-' + openKey)?.focus();
        close();
      }
    };
    const onPointer = (event: PointerEvent) => {
      if (!row.current?.contains(event.target as Node)) {
        close();
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [openKey, close]);

  return (
    <ul className='siteNavMenus' ref={row}>
      {groups.map(({ group, pages }) => {
        const isOpen = openKey === group.key;
        return (
          <li
            key={group.key}
            className={'siteNavItem' + (isOpen ? ' isOpen' : '')}
            onPointerEnter={(event) => {
              if (event.pointerType === 'mouse') {
                cancelClose();
                setOpenKey(group.key);
              }
            }}
            onPointerLeave={(event) => {
              if (event.pointerType === 'mouse') {
                cancelClose();
                closeTimer.current = setTimeout(() => setOpenKey(null), HOVER_CLOSE_MS);
              }
            }}
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget as Node)) {
                setOpenKey((key) => (key === group.key ? null : key));
              }
            }}
          >
            <button
              id={'navButton-' + group.key}
              type='button'
              className={'siteNavTrigger' + (group.key === currentGroup ? ' isCurrent' : '')}
              aria-expanded={isOpen}
              aria-controls={'navMenu-' + group.key}
              onClick={() => {
                cancelClose();
                setOpenKey(isOpen ? null : group.key);
              }}
            >
              {group.heading}
              <span className='siteNavCaret' aria-hidden='true'>▾</span>
            </button>
            <ul
              id={'navMenu-' + group.key}
              className={'siteNavDropdown' + (pages.length > 8 ? ' siteNavDropdown--wide' : '')}
              hidden={!isOpen}
            >
              {pages.map((route) => (
                <li key={route.slug}>
                  <a href={route.slug} aria-current={route.slug === pathname ? 'page' : undefined}>
                    {route.h1}
                  </a>
                </li>
              ))}
            </ul>
          </li>
        );
      })}
    </ul>
  );
}

export default SiteNav;
